<template>
  <bj-modal
    v-if="currentProperty.id"
    id="property-more-details-modal"
    v-model="opened"
    :title="`${editMode ? 'Edit ' : ''}${currentProperty ? currentProperty.title : ''} details`"
  >
    <div slot="content">
      <div
        v-if="editMode"
        class="row gutter-md justify-center q-mb-lg"
      >
        <div class="col-12 text-left">
          <p v-if="hasAppfolioNewIntergation">
            AppFolio integration is enabled, so many of these attributes will not be able to be edited within Showdigs.
          </p>
          <bj-form
            ref="propertyForm"
            :fields="fields"
            :data="currentProperty"
            @keyup.enter.native="saveDetails"
          />
        </div>
        <div class="col-xl-4 col-md-6 col-xs-12">
          <bj-picture-uploader
            :aspect-ratio="16 / 9"
            :url.sync="currentProperty.picture"
            file-name-only
          />
        </div>
      </div>
      <div v-else>
        <ul>
          <li>
            <strong>Prospect communication:</strong>
            <span v-if="currentProperty.business.should_show_contact">
              business contact details:
              <a
                v-if="currentProperty.business.contact_email"
                :href="`mailto:${currentProperty.business.contact_email}`"
                class="q-mr-sm"
              >
                {{ currentProperty.business.contact_email }}
              </a>
              <a
                v-if="currentProperty.business.formatted_contact_phone"
                :href="`tel:${currentProperty.business.formatted_contact_phone.replace(' ext ', ';')}`"
              >
                {{ currentProperty.business.formatted_contact_phone }}
              </a>
            </span>
            <span v-else>
              listing owner
              <a :href="`tel:${currentProperty.listing_manager.formatted_public_phone_number}`">
                {{ currentProperty.listing_manager.formatted_public_phone_number }}
              </a>
            </span>
          </li>
          <li v-if="currentProperty.common_agent_notes">
            <strong>Property-wide instructions:</strong>
            {{ currentProperty.common_agent_notes }}
          </li>
          <li>
            <strong>Agent Availability:</strong>
            <span :class="agentAvailabilityColor">{{ agentAvailability }}</span>
          </li>
        </ul>
      </div>
      <units-list
        v-if="withUnitsList"
        :has-appfolio-new-intergation="hasAppfolioNewIntergation"
        :edit-mode="editMode"
        :validate-prop="$v.currentProperty"
        :current-property="$v.currentProperty.$model"
      />
      <div
        v-if="editMode"
        slot="footer"
        class="q-py-sm"
      >
        <q-btn
          rounded
          label="Save Property Details"
          color="secondary"
          icon="save"
          @click="saveDetails"
        />
      </div>
    </div>
  </bj-modal>
</template>

<script>
import { requiredIf, url } from 'vuelidate/lib/validators';
import { ShowingTypes } from '@/constants/ShowingProfile';
import UnitsList from './units/list';

export default {
  name: 'DetailsModal',
  components: { UnitsList },
  props: {
    currentProperty: {
      type: Object,
      default: {},
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    withUnitsList: {
      type: Boolean,
      default: true,
    },
    modalVisible: {
      type: Boolean,
      default: false,
    },
  },
  validations() {
    return {
      currentProperty: {
        units: {
          $each: {
            name: {},
            last_showing_profile: {
              application_form_url: { required: requiredIf((last_showing_profile) => last_showing_profile), url },
              property_url: { required: requiredIf((last_showing_profile) => last_showing_profile && last_showing_profile.property_url), url },
              is_vacant: { required: requiredIf((last_showing_profile) => last_showing_profile) },
              lock_box_id: {},
              lock_box_code: {
                required: requiredIf((last_showing_profile) => last_showing_profile
                  && !last_showing_profile.comments
                  && last_showing_profile.type !== ShowingTypes.PUBLISH_LISTING),
              },
              comments: {},
              paused_until: {},
            },
          },
        },
      },
    };
  },
  data() {
    const hasAppfolioNewIntergation = !!this.currentProperty.origin_id;

    return {
      hasAppfolioNewIntergation,
      opened: true,
      fields: [
        {
          name: 'pms_code',
          label: 'AppFolio property code',
          type: 'text',
          class: 'col-xs-12',
          disable: () => hasAppfolioNewIntergation,
        },
        {
          name: 'address',
          label: 'Property Address',
          type: 'address',
          rules: ['required'],
          condition: () => this.modalVisible,
        },
        {
          name: 'listing_manager_id',
          label: 'Property Listing Owner',
          type: 'select',
          class: 'col-12',
          optionsResolver: () => this.api()
            .request('GET', `businesses/${this.currentProperty.business_id}/users`, {}, {
              paginate: 0,
              scopes: {
                verified: 1,
              },
            })
            .then((response) => response.map((user) => ({
              value: user.id,
              label: `${user.full_name} - ${user.email}${user.phone_number ? ` - ${user.phone_number}` : ''}`,
            }))),
          rules: ['required'],
        },
        {
          name: 'common_agent_notes',
          label: 'Property-wide instructions',
          type: 'textarea',
          class: 'col-xs-12',
        },
      ],
    };
  },
  computed: {
    agentAvailability() {
      if (this.currentProperty.market_id != null) return 'High';

      const coverage = this.currentProperty.showami_coverage;
      const probability = Number(coverage && coverage.prediction != null ? coverage.prediction : 0.99);

      if (probability >= 0.90) return 'High';
      if (probability >= 0.75) return 'Medium';
      if (probability >= 0.30) return 'Low';
      return 'Very Low';
    },
    agentAvailabilityColor() {
      if (this.agentAvailability === 'High') return 'text-green';
      if (this.agentAvailability === 'Medium') return 'text-orange';
      return 'text-red';
    },
  },
  watch: {
    opened(newValue) {
      if (newValue !== true) {
        this.opened = newValue;
        this.$root.$emit('close-properties-modal');
      }
    },
  },
  methods: {
    saveDetails() {
      this.$v.currentProperty.$touch();
      this.$refs.propertyForm.validate().then(() => {
        if (!this.$v.currentProperty.$invalid) {
          this.uiLoading();
          const url = `admin/properties/${this.currentProperty.id}`;
          const data = {
            common_agent_notes: this.currentProperty.common_agent_notes,
            pms_code: this.currentProperty.pms_code,
            address: this.currentProperty.address,
            picture: this.currentProperty.picture,
            units: this.currentProperty.units,
            listing_manager_id: this.currentProperty.listing_manager_id,
          };

          this.api().request('PUT', url, data).then(() => {
            this.uiNotify('Property details saved successfully');
            this.$root.$emit('close-properties-modal');
            this.$root.$emit('update-properties-table');
            this.uiLoading(false);
          }).catch((error) => {
            this.uiNotify(this.getFirstError(error) || error.message || 'Error occurred', 'negative');
            this.uiLoading(false);
          });
        } else {
          this.uiNotify('Please fill the form', 'negative');
        }
      }).catch(() => {
        this.uiNotify('Please fill the form', 'negative');
      });
    },
  },
};
</script>

<style scoped></style>
